<template>
  <div class="click-link-form">
    <FilterGlobalInformations
      v-model="commonFields"
      v-model:form-validation="formValidationErrors"
    />

    <div class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <ButtonGroup
            v-model="grantAdditionalDelay"
            :options="delayOptions"
            title="automatedScenarios.fields.grantAdditionalDelay"
          />
          <FieldErrors
            :key="componentFieldErrorsKey"
            :errors="formValidationErrors"
            field="grant_additional_delay"
          />
        </div>
      </div>
    </div>

    <div
      v-if="grantAdditionalDelay === '1'"
      class="field-group pt-2"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <PeriodSelector
            v-model="delay"
            :units="units"
            title="automatedScenarios.fields.duration"
          />
          <FieldErrors
            :key="componentFieldErrorsKey"
            :errors="formValidationErrors"
            field="extra_time"
          />
        </div>
      </div>
    </div>

    <div class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <ButtonGroup
            v-model="type"
            :options="typeOptions"
            title="automatedScenarios.fields.customerAction"
          />
          <FieldErrors
            :key="componentFieldErrorsKey"
            :errors="formValidationErrors"
            field="type"
          />
        </div>
      </div>
    </div>

    <div class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <LinkedOperatorSelector
            v-model="linkedBoxArray"
            :linkable-types="linkableTypes"
            :operator-id="operatorId"
            :form-validation="formValidationErrors"
          />
        </div>
      </div>
    </div>

    <div v-if="linkedBoxArray.length > 0" class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <FunnelStepSelector
            v-model="funnelTemplateIds"
            :linked-box="linkedBoxArray"
            :operator-id="operatorId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ButtonGroup from '@/components/automated-scenarios/fields/ButtonGroup.vue';
import PeriodSelector from '@/components/automated-scenarios/fields/PeriodSelector.vue';
import { FilterGlobalInformations, LinkedOperatorSelector } from '@/components/flows/operators/shared';
import type { Period } from '@/types/flow-types';
import { createDefaultData, LINKABLE_OPERATOR_TYPES, ClickLinkData } from './index';
import FunnelStepSelector from './FunnelStepSelector.vue';

export default defineComponent({
  name: 'ClickLinkForm',

  components: {
    FieldErrors,
    ButtonGroup,
    PeriodSelector,
    FilterGlobalInformations,
    LinkedOperatorSelector,
    FunnelStepSelector,
  },

  props: {
    modelValue: {
      type: Object as PropType<ClickLinkData>,
      required: true,
      default: () => createDefaultData(),
    },
    formValidation: {
      type: Object,
      default: () => ({}),
    },
    operatorId: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const commonFields = ref({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: Number(props.modelValue.outputs_number),
      initial_number_outputs: props.modelValue.initial_number_outputs,
    });

    const grantAdditionalDelay = ref(props.modelValue.grant_additional_delay);
    const delay = ref<Period>({
      unit: props.modelValue.extra_time?.unit ?? 'DAY',
      value: props.modelValue.extra_time?.value ?? 1,
    });

    const type = ref(props.modelValue.type);

    const parseLinkedBox = (): string[] => {
      const linkedBox = props.modelValue.linked_box;
      // Handle both string (from storage) and array (already parsed by parseOperatorParameters)
      if (Array.isArray(linkedBox)) {
        return linkedBox;
      }
      if (typeof linkedBox === 'string' && linkedBox) {
        try {
          const parsed = JSON.parse(linkedBox);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    const linkedBoxArray = ref<string[]>(parseLinkedBox());

    const parseFunnelTemplateIds = (): number[] => {
      const raw = props.modelValue.funnel_template_ids;
      if (Array.isArray(raw)) return raw.map(Number);
      if (typeof raw === 'string' && raw) {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed.map(Number) : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    const funnelTemplateIds = ref<number[]>(parseFunnelTemplateIds());

    const delayOptions = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];

    const typeOptions = [
      { value: 'click', label: t('automatedScenarios.fields.actions.clicked') },
      { value: 'noclick', label: t('automatedScenarios.fields.actions.notClicked') },
    ];

    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
    ];

    const linkableTypes = LINKABLE_OPERATOR_TYPES;

    const update = () => {
      emit('update:modelValue', createDefaultData({
        ...commonFields.value,
        grant_additional_delay: grantAdditionalDelay.value,
        extra_time: delay.value,
        type: type.value as 'click' | 'noclick',
        linked_box: JSON.stringify(linkedBoxArray.value),
        funnel_template_ids: JSON.stringify(funnelTemplateIds.value),
      }));
    };

    watch([commonFields, grantAdditionalDelay, delay, type, linkedBoxArray, funnelTemplateIds], update, { deep: true });

    return {
      t,
      formValidationErrors,
      componentFieldErrorsKey,
      commonFields,
      grantAdditionalDelay,
      delay,
      type,
      linkedBoxArray,
      funnelTemplateIds,
      delayOptions,
      typeOptions,
      units,
      linkableTypes,
    };
  },
});
</script>

<style lang="scss" scoped>
.click-link-form {
  .field-group {
    margin-bottom: 1rem;
  }

  .field-wrapper {
    margin-bottom: 0.5rem;
  }
}
</style>
